import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { AlertDialog, ChoiceDialog, type ChoiceOption } from "../components/ui/alertDialog.tsx";

export interface AlertDialogOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export interface ChoiceDialogOptions<T> {
  title: string;
  body?: ReactNode;
  options: ChoiceOption<T>[];
  cancelLabel?: string;
}

interface AlertDialogContextValue {
  /**
   * Show a confirm dialog. Resolves to `true` if the user confirmed, `false`
   * if they cancelled (ESC / Cancel button / backdrop click).
   *
   *   const { confirm } = useAlertDialog();
   *   if (await confirm({ title: "Delete?", destructive: true })) { ... }
   */
  confirm: (opts: AlertDialogOptions) => Promise<boolean>;
  /**
   * Show a 2+ option decision dialog. Resolves to the chosen option's `value`
   * or `null` if the user cancelled.
   *
   *   const choice = await choose<"keep" | "discard">({
   *     title: "Unsaved changes",
   *     options: [
   *       { label: "Keep", value: "keep", variant: "primary", default: true },
   *       { label: "Discard", value: "discard", variant: "danger" },
   *     ],
   *   });
   */
  choose: <T>(opts: ChoiceDialogOptions<T>) => Promise<T | null>;
}

const AlertDialogContext = createContext<AlertDialogContextValue | null>(null);

interface ConfirmPending {
  opts: AlertDialogOptions;
  resolve: (ok: boolean) => void;
}

interface ChoicePending {
  opts: ChoiceDialogOptions<unknown>;
  resolve: (value: unknown | null) => void;
}

export function AlertDialogProvider({ children }: { children: ReactNode }) {
  const [confirmPending, setConfirmPending] = useState<ConfirmPending | null>(null);
  const [choicePending, setChoicePending] = useState<ChoicePending | null>(null);
  const confirmResolveRef = useRef<((ok: boolean) => void) | null>(null);
  const choiceResolveRef = useRef<((v: unknown | null) => void) | null>(null);

  const confirm = useCallback((opts: AlertDialogOptions) => {
    return new Promise<boolean>((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmPending({ opts, resolve });
    });
  }, []);

  const choose = useCallback(<T,>(opts: ChoiceDialogOptions<T>): Promise<T | null> => {
    return new Promise<T | null>((resolve) => {
      choiceResolveRef.current = resolve as (v: unknown | null) => void;
      setChoicePending({
        opts: opts as ChoiceDialogOptions<unknown>,
        resolve: resolve as (v: unknown | null) => void,
      });
    });
  }, []);

  const closeConfirm = useCallback((result: boolean) => {
    const r = confirmResolveRef.current;
    confirmResolveRef.current = null;
    setConfirmPending(null);
    r?.(result);
  }, []);

  const closeChoice = useCallback((value: unknown | null) => {
    const r = choiceResolveRef.current;
    choiceResolveRef.current = null;
    setChoicePending(null);
    r?.(value);
  }, []);

  return (
    <AlertDialogContext.Provider value={{ confirm, choose }}>
      {children}
      <AlertDialog
        open={confirmPending !== null}
        title={confirmPending?.opts.title ?? ""}
        body={confirmPending?.opts.body}
        confirmLabel={confirmPending?.opts.confirmLabel}
        cancelLabel={confirmPending?.opts.cancelLabel}
        destructive={confirmPending?.opts.destructive ?? false}
        onCancel={() => closeConfirm(false)}
        onConfirm={() => closeConfirm(true)}
      />
      <ChoiceDialog
        open={choicePending !== null}
        title={choicePending?.opts.title ?? ""}
        body={choicePending?.opts.body}
        options={choicePending?.opts.options ?? []}
        cancelLabel={choicePending?.opts.cancelLabel}
        onCancel={() => closeChoice(null)}
        onChoose={(v) => closeChoice(v)}
      />
    </AlertDialogContext.Provider>
  );
}

export function useAlertDialog(): AlertDialogContextValue {
  const ctx = useContext(AlertDialogContext);
  if (!ctx) throw new Error("useAlertDialog must be used within an AlertDialogProvider");
  return ctx;
}
